import React, { useEffect } from "react";
import { Skeleton } from "../../components/ui/Skeleton";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { usePublicBranding } from "../../hooks/usePublicBranding";
import { getPortalSubdomain } from "../../utils/portalSubdomain";
import PortalBillingSection from "./PortalBillingSection";

function toTitleCase(str: string | undefined | null): string {
  if (!str?.trim()) {
    return "";
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function getScoreBorderClass(score: number): string {
  if (score >= 750) {
    return "border-l-emerald-500";
  }
  if (score >= 700) {
    return "border-l-emerald-400";
  }
  if (score >= 580) {
    return "border-l-amber-500";
  }
  return "border-l-red-500";
}

function getScoreLabel(score: number): string {
  if (score >= 750) {
    return "Excellent";
  }
  if (score >= 700) {
    return "Good";
  }
  if (score >= 580) {
    return "Fair";
  }
  return "Needs work";
}

function getScoreLabelColorClass(score: number): string {
  if (score >= 750) {
    return "text-emerald-600";
  }
  if (score >= 700) {
    return "text-emerald-500";
  }
  if (score >= 580) {
    return "text-amber-600";
  }
  return "text-red-600";
}

const PortalDashboardPage: React.FC = () => {
  const { me, refreshMe } = usePortalAuth();
  const subdomain = me?.subdomain || getPortalSubdomain();
  const { branding } = usePublicBranding(subdomain);

  const operatorName = branding?.businessName || branding?.companyName || me?.operatorBusinessName;

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  if (!me) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label="Loading dashboard">
        <Skeleton className="h-10 w-3/4 max-w-md bg-slate-200/90" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-lg bg-slate-200/90" />
          ))}
        </div>
        <Skeleton className="h-48 w-full rounded-lg bg-slate-200/90" />
      </div>
    );
  }

  const bureauShort: Record<string, string> = {
    Equifax: "EQ",
    Experian: "EX",
    TransUnion: "TU",
  };

  const sectionTitleClass = "mb-3 text-xs font-semibold uppercase tracking-[0.05em] text-slate-600";

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start gap-4">
        {branding?.logoUrl && (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <img src={branding.logoUrl} alt="" className="max-h-full max-w-full object-contain" />
          </div>
        )}
        <div>
          <h1 className="text-2xl font-semibold text-navy-900">Welcome, {toTitleCase(me.firstName)}</h1>
          {branding?.welcomeMessage ? (
            <p className="mt-2 text-sm text-slate-700">{branding.welcomeMessage}</p>
          ) : null}
          <p className="mt-1 text-sm text-slate-600">
            Here’s a simple snapshot of your credit repair progress with {operatorName ?? me.operatorBusinessName}.
          </p>
          {branding?.chatbotName && branding?.aiChatbotEnabled !== false && (
            <p className="mt-2 text-xs text-slate-600">
              Need help? Chat with{" "}
              <span className="font-medium text-emerald-500">{branding.chatbotName}</span> anytime.
            </p>
          )}
        </div>
      </div>

      {me.referFriend?.enabled && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-navy-900">Refer a friend</h2>
          <p className="mt-1 text-sm text-slate-600">
            Know someone who needs credit repair? Share your link — you both earn rewards from {operatorName ?? "your advisor"}.
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <input
              readOnly
              value={me.referFriend.referralLink}
              className="min-w-0 w-full flex-1 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-navy-900 sm:min-w-[200px]"
            />
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(me.referFriend!.referralLink)}
              className="w-full rounded px-4 py-2 text-sm font-semibold text-white hover:opacity-90 sm:w-auto"
              style={{ backgroundColor: "var(--portal-primary)" }}
            >
              Copy link
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-6 text-sm text-slate-600">
            <span>
              Your reward:{" "}
              <span className="font-medium text-emerald-600">
                ${me.referFriend.referrerRewardAmount.toFixed(0)} {me.referFriend.referrerRewardType}
              </span>
            </span>
            <span>
              Their reward:{" "}
              <span className="font-medium text-emerald-600">
                ${me.referFriend.newClientRewardAmount.toFixed(0)} off ({me.referFriend.newClientRewardType})
              </span>
            </span>
          </div>
          <p className="mt-3 text-sm text-slate-500">
            {me.referFriend.referralCount} friends referred · {me.referFriend.convertedCount} signed up
          </p>
        </section>
      )}

      <section>
        <h2 className={sectionTitleClass}>Your scores</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {me.scores.map((s) => {
            const score = s.currentScore;
            const borderLeft = getScoreBorderClass(score);
            return (
              <div
                key={s.bureau}
                className={`rounded-lg border border-slate-200 border-l-4 bg-white p-5 shadow-sm ${borderLeft}`}
              >
                <p className="mb-1 text-xs uppercase tracking-wide text-slate-500">
                  {bureauShort[s.bureau] ?? s.bureau}
                </p>
                <p className="text-3xl font-semibold tabular-nums text-navy-900">{score}</p>
                <div className="mt-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm font-medium ${getScoreLabelColorClass(score)}`}>{getScoreLabel(score)}</span>
                    {s.improvementPoints > 0 && (
                      <span className="shrink-0 text-xs font-medium text-emerald-600">+{s.improvementPoints} pts</span>
                    )}
                  </div>
                  {s.improvementPoints === 0 && (
                    <p className="text-xs text-slate-500">Baseline — we’ll show gains as they happen</p>
                  )}
                  {s.improvementPoints < 0 && (
                    <p className="text-xs text-amber-800">{s.improvementPoints} pts vs. first snapshot</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h2 className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-600">Repair progress</h2>
            <p className="mt-1 text-sm text-slate-600">
              Estimated completion based on disputed items we’ve resolved for you.
            </p>
          </div>
          <span className="shrink-0 text-2xl font-bold tabular-nums text-navy-600">{me.repairProgressPercent}%</span>
        </div>
        <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${Math.min(100, Math.max(0, me.repairProgressPercent))}%` }}
          />
        </div>
      </section>

      <PortalBillingSection />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-600">Active disputes</h2>
          <ul className="mt-4 space-y-3">
            {me.activeDisputes.length === 0 && (
              <li className="rounded-lg bg-slate-50 px-3 py-4 text-sm text-slate-600">
                No open disputes right now — we’ll post updates here when there’s news.
              </li>
            )}
            {me.activeDisputes.map((d) => (
              <li
                key={d.id}
                className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 transition-colors hover:bg-slate-100"
              >
                <p className="font-medium text-slate-800">{d.creditor}</p>
                <p className="mt-1 text-sm font-medium text-navy-600">{d.statusLabel}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-600">Recent activity</h2>
          <ul className="mt-4 space-y-4">
            {me.recentActivity.length === 0 && <li className="text-sm text-slate-600">No recent updates yet.</li>}
            {me.recentActivity.map((a, idx) => (
              <li key={`${a.at}-${idx}`} className="flex gap-3 border-b border-slate-200 pb-4 last:border-0 last:pb-0">
                <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                <div>
                  <p className="text-sm font-medium text-slate-800">{a.title}</p>
                  <p className="text-xs text-slate-600">{a.detail}</p>
                  <p className="mt-1 text-xs text-slate-400">
                    {new Date(a.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default PortalDashboardPage;
